import React, { useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import BTable from 'react-bootstrap/Table';
import cx from 'classnames';
import { format } from 'date-fns';
import { ComplianceMarker } from './compliance-marker';
import { Faults } from './faults';
import { statusText } from '../utils/texts';
import ArrowIcon from '../icons/arrow';

function createTableData(data: [string, Status.Item][]) {
  return data.map(([topic, message]) => {
    const topicItems = topic.split('/');
    return {
      operator: topicItems[0],
      vin: topicItems[2],
      updated: message.eventTimestamp,
      status: message.journeyStatus,
      compliance: message.compliance,
      faults: message.complianceFaults,
      totalDisplayCount: message.totalDisplayCount,
      liveDisplayCount: message.liveDisplayCount,
      expectedDisplayCount: message.expectedDisplayCount,
    };
  });
}

interface Props {
  data: [string, Status.Item][];
}
export function StatusTable({ data }: Props) {
  const tableData = useMemo(() => createTableData(data), [data]);
  const columns = useMemo(
    () => [
      {
        Header: 'Operator',
        accessor: 'operator',
        sortDescFirst: true,
      },
      {
        Header: 'Vehicle',
        accessor: 'vin',
        sortDescFirst: true,
      },
      {
        Header: 'Updated',
        accessor: 'updated',
        sortDescFirst: true,
        Cell: ({ value }) => {
          return format(new Date(value), 'dd. MMM yyyy - HH:mm');
        },
      },
      {
        Header: 'Journey Status',
        accessor: 'status',
        sortDescFirst: true,
        Cell: ({ value }) => {
          return statusText[value] || value;
        },
      },
      {
        Header: 'Compliance',
        columns: [
          {
            Header: 'Status',
            accessor: 'compliance',
            sortDescFirst: true,
            Cell: ({ value }) => {
              return <ComplianceMarker compliance={value} />;
            },
          },
          {
            Header: 'Faults',
            accessor: 'faults',
            sortDescFirst: true,
            Cell: ({ value }) => {
              return <Faults faults={value} />;
            },
            sortType: function (rowA, rowB, colId) {
              const a = rowA.values[colId].length;
              const b = rowB.values[colId].length;
              if (a === b) return 0;
              return a > b ? 1 : -1;
            },
          },
        ],
      },
      {
        Header: 'Displays',
        columns: [
          {
            Header: 'Total',
            accessor: 'totalDisplayCount',
            sortDescFirst: true,
          },
          {
            Header: 'Live',
            accessor: 'liveDisplayCount',
            sortDescFirst: true,
          },
          {
            Header: 'Expected',
            accessor: 'expectedDisplayCount',
            sortDescFirst: true,
          },
        ],
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
      autoResetSortBy: false,
      disableSortRemove: true,
      initialState: {
        sortBy: [
          {
            id: 'updated',
            desc: true,
          },
        ],
      },
    },
    useSortBy
  );

  return (
    <BTable striped hover {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                {column.render('Header')}
                {column.accessor && (
                  <span>
                    <ArrowIcon
                      className={cx('filter-arrow', {
                        '--visible': column.isSorted,
                        '--reversed': column.isSortedDesc,
                      })}
                    />
                  </span>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell) => {
                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </BTable>
  );
}
